// Central registry of document types for Rental P.A.
//
// Each entry has:
//   value:    machine key (snake_case) - matches backend `document_type`
//   label:    human label shown in the UI
//   category: section in the picker
//   iconName: lucide-react icon name (resolved by the consumer)
//   renewal:  renewal cadence (annual | 2y | 5y | 10y | per_tenancy | none)
//   hmoOnly:  true => only relevant for HMO properties
//   isPremium: true => the document carries a £ amount (insurance, cover, etc.)
//
// Order within each category is intentional - reflects compliance priority.

export const RENEWAL_LABELS = {
  annual: 'Annual',
  '2y': 'Every 2 years',
  '5y': 'Every 5 years',
  '10y': 'Every 10 years',
  per_tenancy: 'Per tenancy',
  none: 'No expiry',
};

export const RENEWAL_DAYS = {
  annual: 365,
  '2y': 730,
  '5y': 1825,
  '10y': 3650,
  per_tenancy: null,
  none: null,
};

export const DOC_CATEGORIES = [
  { key: 'mandatory', label: 'Mandatory — All Properties' },
  { key: 'hmo',       label: 'HMO Additional' },
  { key: 'insurance', label: 'Insurance' },
  { key: 'other',     label: 'Other' },
];

export const DOC_TYPES = [
  // Mandatory — all properties
  { value: 'gas_safety',                 label: 'Gas Safety Certificate',                 category: 'mandatory', iconName: 'Flame',         renewal: 'annual' },
  { value: 'eicr',                       label: 'Electrical Installation Condition Report (EICR)', category: 'mandatory', iconName: 'Plug', renewal: '5y' },
  { value: 'epc',                        label: 'Energy Performance Certificate (EPC)',   category: 'mandatory', iconName: 'Zap',           renewal: '10y' },
  { value: 'deposit_protection',         label: 'Deposit Protection Certificate',         category: 'mandatory', iconName: 'Shield',        renewal: 'per_tenancy' },
  { value: 'how_to_rent_guide',          label: 'How to Rent Guide',                      category: 'mandatory', iconName: 'FileText',      renewal: 'per_tenancy' },
  { value: 'smoke_alarm_test',           label: 'Smoke Alarm Test Record',                category: 'mandatory', iconName: 'FileCheck',     renewal: 'per_tenancy' },
  { value: 'carbon_monoxide_alarm',      label: 'Carbon Monoxide Alarm Record',           category: 'mandatory', iconName: 'FileCheck',     renewal: 'per_tenancy' },

  // HMO Additional - only shown when property_type === 'HMO'
  { value: 'hmo_licence',                label: 'HMO Licence',                            category: 'hmo', iconName: 'Shield',        renewal: '5y',     hmoOnly: true },
  { value: 'fire_risk_assessment',       label: 'Fire Risk Assessment',                   category: 'hmo', iconName: 'Flame',         renewal: 'annual', hmoOnly: true },
  { value: 'fire_alarm_service',         label: 'Fire Alarm Service Record',              category: 'hmo', iconName: 'Flame',         renewal: 'annual', hmoOnly: true },
  { value: 'emergency_lighting_test',    label: 'Emergency Lighting Test Record',         category: 'hmo', iconName: 'Zap',           renewal: 'annual', hmoOnly: true },
  { value: 'pat_testing',                label: 'PAT Testing Record',                     category: 'hmo', iconName: 'Plug',          renewal: 'annual', hmoOnly: true },

  // Insurance
  { value: 'buildings_insurance',        label: 'Buildings Insurance',                    category: 'insurance', iconName: 'Shield',        renewal: 'annual', isPremium: true },
  { value: 'landlord_insurance',         label: 'Landlord Insurance',                     category: 'insurance', iconName: 'Shield',        renewal: 'annual', isPremium: true },
  { value: 'rent_guarantee_insurance',   label: 'Rent Guarantee Insurance',               category: 'insurance', iconName: 'Shield',        renewal: 'annual', isPremium: true },
  { value: 'boiler_cover',               label: 'Boiler Service/Cover',                   category: 'insurance', iconName: 'Flame',         renewal: 'annual', isPremium: true },

  // Other
  { value: 'tenancy_agreement',          label: 'Tenancy Agreement',                      category: 'other', iconName: 'FileSignature', renewal: 'per_tenancy' },
  { value: 'boiler_service',             label: 'Boiler Service Record',                  category: 'other', iconName: 'Flame',         renewal: 'annual' },
  { value: 'legionella_risk_assessment', label: 'Legionella Risk Assessment',             category: 'other', iconName: 'Droplet',       renewal: '2y' },
  { value: 'leasehold',                  label: 'Leasehold Documents',                    category: 'other', iconName: 'FileText',      renewal: 'none' },
  { value: 'appliance_guarantees',       label: 'Appliance Guarantees',                   category: 'other', iconName: 'FileCheck',     renewal: 'none' },
];

export const PROPERTY_TYPES = [
  { value: 'AST',        label: 'AST (Standard residential let)' },
  { value: 'HMO',        label: 'HMO (House in Multiple Occupation)' },
  { value: 'Licensed',   label: 'Licensed property' },
  { value: 'Commercial', label: 'Commercial' },
];

// Build a Map<value, type> for O(1) lookup.
const TYPE_BY_VALUE = DOC_TYPES.reduce((acc, t) => { acc[t.value] = t; return acc; }, {});

export const getDocType = (value) => TYPE_BY_VALUE[value] || null;

export const docHasPremium = (typeValue, typeLabel = '') => {
  const t = TYPE_BY_VALUE[typeValue];
  if (t?.isPremium) return true;
  // Heuristic for custom doc types added by the user
  const haystack = `${typeValue} ${typeLabel}`.toLowerCase();
  return /insurance|cover|guarantee/.test(haystack);
};

// Returns the visible doc types for a given property type.
// HMO-only entries are filtered out unless property_type === 'HMO'.
export const getDocTypesForProperty = (propertyType) => {
  return DOC_TYPES.filter(t => !t.hmoOnly || propertyType === 'HMO');
};

// Per-tenancy doc types that should re-sync their expiry date when a new
// tenancy is saved. Tenancy Agreement itself is excluded — its expiry is set
// directly by the upload flow.
export const PER_TENANCY_SYNCABLE_TYPES = DOC_TYPES
  .filter(t => t.renewal === 'per_tenancy' && t.value !== 'tenancy_agreement')
  .map(t => ({ value: t.value, label: t.label }));

// Groups types by category, in DOC_CATEGORIES order, filtered for the given property.
export const getGroupedDocTypes = (propertyType) => {
  const visible = getDocTypesForProperty(propertyType);
  return DOC_CATEGORIES
    .map(cat => ({ ...cat, items: visible.filter(t => t.category === cat.key) }))
    .filter(g => g.items.length > 0);
};
